//
// # InstanceDialog
//
// A unified dialog for adding new instances and editing existing instance
// parameters. Operates in two modes:
//
// "new" mode (from right-click "Add Instance" or command palette):
//  1. Symbol picker — filterable list with two-line rows (name + description)
//  2. Parameters (if parametric) — form for generator params
//  3. Name input — optional custom instance name
//  4. Calls create-instance
//
// "edit" mode (from right-click "Edit Parameters..."):
//  1. Parameter form pre-filled with current instance values
//  2. Calls update-instance
//
// Styled like the CommandPalette, rendered as a portal overlay.
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { paramValueToString } from "CoreUtils";
import type { SymbolInfo, GeneratorParamSpec } from "ServerInterface";
import type { InstanceDialogState } from "../../types";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { extractErrorMessage } from "../../errorUtil";
import {
  ParamFields,
  convertParamValues,
  omitDefaultParams,
} from "../common/ParamFields";

// ------------------------------------------------------------------ //
// Step enum (used in "new" mode only)                                  //
// ------------------------------------------------------------------ //

type Step =
  | { kind: "pick-symbol" }
  // Waiting on `get-symbol-param-spec` for the symbol just picked. The list
  // response no longer carries param specs (they got large enough to dominate
  // it — see that request's doc in ServerInterface), so this is a real
  // round-trip the dialog has to show.
  | { kind: "loading-params"; symbol: SymbolInfo }
  | { kind: "params"; symbol: SymbolInfo; paramSpec: GeneratorParamSpec }
  // `paramSpec` rides along so Escape can step back into the params form.
  // It used to be read off `symbol.paramSpec`, which the symbol list no
  // longer carries; absent here means the symbol genuinely has no params and
  // Escape goes back to the picker.
  | {
      kind: "name";
      symbol: SymbolInfo;
      paramValues: Record<string, unknown>;
      paramSpec?: GeneratorParamSpec;
    };

// Segment-arrangement control for multi-finger devices. Shown only when there
// is an arrangement to choose: m > 1 in the form, or an instance that is
// already segmented. Not a generator param — it is a placement input sent per
// call and never stored on the instance.
function ShapeField({
  rectangular,
  setRectangular,
}: {
  rectangular: boolean;
  setRectangular: (v: boolean) => void;
}) {
  return (
    <label className="add-instance-shape-field">
      <input
        type="checkbox"
        checked={rectangular}
        onChange={(e) => {
          setRectangular(e.target.checked);
        }}
      />
      Arrange segments as a grid (default: a single row)
    </label>
  );
}

// ------------------------------------------------------------------ //
// Props                                                                //
// ------------------------------------------------------------------ //

export interface InstanceDialogProps {
  readonly state: InstanceDialogState;
  readonly onClose: () => void;
}

// ------------------------------------------------------------------ //
// Main component                                                       //
// ------------------------------------------------------------------ //

export function InstanceDialog({ state, onClose }: InstanceDialogProps) {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const mode = state.mode;
  // Segment arrangement is a layout concern; a schematic instance has none.
  const isLayoutDesign =
    state.designIdent.kind === "hierpath" &&
    state.designIdent.path[state.designIdent.path.length - 1] === "layout";

  // -- Shared state --
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Segment arrangement for m > 1 devices. Seeded from the instance's actual
  // shape in "edit" mode, so leaving it alone re-sends the shape it already
  // has rather than silently re-gridding it.
  const [rectangular, setRectangular] = useState(false);

  // -- "new" mode state --
  const [step, setStep] = useState<Step>({ kind: "pick-symbol" });
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [instanceName, setInstanceName] = useState("");

  // -- "rename" mode state --
  const [renameValue, setRenameValue] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);
  // Name of the symbol whose param-spec fetch is currently in flight, so a
  // superseded fetch can't overwrite the step when it lands late.
  const pendingSymbolRef = useRef<string | null>(null);

  // Store the previously focused element so we can restore it on close.
  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    return () => {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  // Auto-focus the input when the dialog opens or step changes.
  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  // Initial form values for a spec: the declared default, else the first enum
  // option (so the form matches what the select actually displays), else blank.
  const defaultsFor = useCallback(
    (paramSpec: GeneratorParamSpec): Record<string, string> => {
      const defaults: Record<string, string> = {};
      for (const p of paramSpec.params) {
        if (p.default !== undefined) {
          // `default` is `unknown`; `paramValueToString` renders the primitive
          // cases and refuses anything that would stringify to "[object
          // Object]".
          defaults[p.name] = paramValueToString(p.default).unwrapOr("");
        } else if (p.type === "enum" && p.constraints?.enum?.length) {
          defaults[p.name] = paramValueToString(p.constraints.enum[0]).unwrapOr(
            ""
          );
        } else {
          defaults[p.name] = "";
        }
      }
      return defaults;
    },
    []
  );

  // Advance from a picked symbol to the params step (or straight to the name
  // step when it has none), fetching the spec if the caller didn't supply one.
  //
  // `symbol.paramSpec` is normally absent: `list-available-symbols` stopped
  // carrying specs once external cells started declaring their real CDF
  // params. It's still honoured when present so a caller that already has a
  // spec doesn't pay for a second round-trip.
  const advanceToParams = useCallback(
    async (symbol: SymbolInfo) => {
      setError(null);
      if (!symbol.hasParams) {
        setStep({ kind: "name", symbol, paramValues: {} });
        return;
      }
      if (symbol.paramSpec) {
        setParamValues(defaultsFor(symbol.paramSpec));
        setStep({ kind: "params", symbol, paramSpec: symbol.paramSpec });
        return;
      }

      setStep({ kind: "loading-params", symbol });
      pendingSymbolRef.current = symbol.name;
      const result = await workspace.getSymbolParamSpec(
        symbol.name,
        symbol.pdkId
      );
      // The user may have escaped back to the picker, or picked something
      // else, while this was in flight. Only the newest request may touch the
      // step, or a slow fetch would yank the dialog back to a stale symbol.
      if (pendingSymbolRef.current !== symbol.name) return;
      if (result.err) {
        setError(extractErrorMessage(result.val));
        setStep({ kind: "pick-symbol" });
        return;
      }
      const paramSpec = result.val;
      if (!paramSpec || paramSpec.params.length === 0) {
        setStep({ kind: "name", symbol, paramValues: {} });
        return;
      }
      setParamValues(defaultsFor(paramSpec));
      setStep({ kind: "params", symbol, paramSpec });
    },
    [workspace, defaultsFor]
  );

  // -- "new" mode: skip symbol picker when a pre-selected symbol is provided --
  // (e.g. drag-and-drop from PDK primitives section)
  useEffect(() => {
    if (mode.kind !== "new" || !mode.preSelectedSymbol) return;
    // Same path a click through the picker takes — including the param-spec
    // fetch, since a dragged symbol carries no spec either.
    void advanceToParams(mode.preSelectedSymbol);
  }, [mode, advanceToParams]);

  // -- "edit" mode: initialize form values and auto-focus when instance details arrive --
  useEffect(() => {
    if (mode.kind !== "edit") return;
    if (!mode.currentParams || !mode.paramSpec) return;
    const initial: Record<string, string> = {};
    for (const p of mode.paramSpec.params) {
      const current = mode.currentParams[p.name];
      if (current !== undefined) {
        initial[p.name] = String(current);
      } else if (p.default !== undefined) {
        initial[p.name] = String(p.default);
      } else if (p.type === "enum" && p.constraints?.enum?.length) {
        // For enums without a default, use the first option to match what the select displays
        initial[p.name] = String(p.constraints.enum[0]);
      } else {
        initial[p.name] = "";
      }
    }
    setParamValues(initial);
    setRectangular(mode.currentRectangular ?? false);
    inputRef.current?.focus();
  }, [mode]);

  // -- "rename" mode: initialize rename value and auto-focus when current name arrives --
  useEffect(() => {
    if (mode.kind === "rename" && mode.currentName !== null) {
      setRenameValue(mode.currentName);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [mode]);

  // ---------------------------------------------------------------- //
  // "new" mode: symbol filtering and navigation                       //
  // ---------------------------------------------------------------- //

  // Filter symbols by active design's content kind:
  // - Layouts can only instance generators (hasParams === true)
  // - Schematics can instance any symbol (builtins and generators with symbol support)
  const allSymbols = mode.kind === "new" ? mode.symbols ?? [] : [];
  const activeInfo = snapshot.activeDesignInfo;
  const symbols =
    activeInfo?.contentKind === "layout"
      ? allSymbols.filter((s) => s.hasParams)
      : allSymbols;
  const lower = filter.toLowerCase();
  const filteredSymbols =
    mode.kind === "new" && step.kind === "pick-symbol"
      ? symbols.filter(
          (s) =>
            s.name.toLowerCase().includes(lower) ||
            s.description.toLowerCase().includes(lower)
        )
      : [];

  // Separate into PDK-specific and generic symbols.
  const pdkSymbols = filteredSymbols.filter((s) => s.pdkId);
  const genericSymbols = filteredSymbols.filter((s) => !s.pdkId);
  const hasSections = pdkSymbols.length > 0 && genericSymbols.length > 0;

  // Build a flat list of items for navigation. Section headers are not
  // included in the navigable items.
  const allFiltered = hasSections
    ? [...pdkSymbols, ...genericSymbols]
    : filteredSymbols;

  // Clamp selectedIndex when filtered list changes.
  useEffect(() => {
    if (selectedIndex >= allFiltered.length) {
      setSelectedIndex(Math.max(0, allFiltered.length - 1));
    }
  }, [allFiltered.length, selectedIndex]);

  // Scroll the selected row into view.
  useEffect(() => {
    if (!listRef.current || step.kind !== "pick-symbol") return;
    const selectedEl = listRef.current.querySelector(
      `[data-index="${selectedIndex}"]`
    );
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, step.kind]);

  // Handle selecting a symbol ("new" mode).
  const selectSymbol = useCallback(
    (symbol: SymbolInfo) => {
      void advanceToParams(symbol);
    },
    [advanceToParams]
  );

  // Handle submitting the params step ("new" mode).
  const confirmParams = useCallback(() => {
    if (step.kind !== "params") return;
    const converted = convertParamValues(step.paramSpec, paramValues);
    if (!converted.ok) {
      setError(converted.error);
      return;
    }
    setStep({
      kind: "name",
      symbol: step.symbol,
      // New instance — nothing stored to overwrite.
      paramValues: omitDefaultParams(step.paramSpec, converted.val),
      paramSpec: step.paramSpec,
    });
    setError(null);
  }, [step, paramValues]);

  // ---------------------------------------------------------------- //
  // Submit handlers                                                    //
  // ---------------------------------------------------------------- //

  // "new" mode: create instance.
  const submitNewInstance = useCallback(async () => {
    if (mode.kind !== "new" || step.kind !== "name") return;
    setSubmitting(true);
    setError(null);

    const result = await workspace.createInstance({
      designIdent: state.designIdent,
      symbolName: step.symbol.name,
      pdkId: step.symbol.pdkId,
      params:
        Object.keys(step.paramValues).length > 0 ? step.paramValues : undefined,
      position: mode.position,
      name: instanceName.trim() || undefined,
      rectangular,
    });

    setSubmitting(false);
    if (result.err) {
      setError(extractErrorMessage(result.val));
    } else {
      const layerWarning = result.val.layerWarning;
      if (layerWarning) {
        const { unmappedElements, unmappedSpecs } = layerWarning;
        workspace.logToConsole(
          "warn",
          "external-gen",
          `Import of '${step.symbol.name}': ${unmappedElements} element(s) on unmapped layers`
        );
        for (const spec of unmappedSpecs) {
          workspace.logToConsole(
            "warn",
            "external-gen",
            `  layer ${spec.layer}, datatype ${spec.datatype} (${
              spec.count
            } element${spec.count === 1 ? "" : "s"})`
          );
        }
        workspace
          .getActiveEditor()
          ?.showFeedback(
            `Import: ${unmappedElements} element(s) on unmapped layers — see Console`,
            6000
          );
        workspace.showPanel("bottom");
      }
      onClose();
    }
  }, [mode, step, workspace, state, instanceName, rectangular, onClose]);

  // "edit" mode: update instance.
  const submitEditInstance = useCallback(async () => {
    if (mode.kind !== "edit" || !mode.paramSpec) return;
    setSubmitting(true);
    setError(null);

    const converted = convertParamValues(mode.paramSpec, paramValues);
    if (!converted.ok) {
      setSubmitting(false);
      setError(converted.error);
      return;
    }
    const result = await workspace.updateInstance({
      designIdent: state.designIdent,
      instanceId: mode.instanceId,
      // `currentParams` so a field reset to its default still overwrites.
      params: omitDefaultParams(
        mode.paramSpec,
        converted.val,
        mode.currentParams
      ),
      rectangular,
    });

    setSubmitting(false);
    if (result.err) {
      setError(extractErrorMessage(result.val));
    } else {
      onClose();
    }
  }, [mode, state, paramValues, rectangular, workspace, onClose]);

  // "rename" mode: rename instance.
  const submitRenameInstance = useCallback(async () => {
    if (mode.kind !== "rename" || !mode.currentName) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === mode.currentName) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);

    const result = await workspace.updateInstance({
      designIdent: state.designIdent,
      instanceId: mode.instanceId,
      name: trimmed,
    });

    setSubmitting(false);
    if (result.err) {
      setError(extractErrorMessage(result.val));
    } else {
      onClose();
    }
  }, [mode, state, renameValue, workspace, onClose]);

  // ---------------------------------------------------------------- //
  // Keyboard navigation                                                //
  // ---------------------------------------------------------------- //

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Prevent keyboard events from reaching EditorCore's global key handler,
      // which would otherwise interpret them as canvas shortcuts (e.g. 'F' zooms to fit).
      e.stopPropagation();

      if (e.key === "Escape") {
        if (mode.kind === "new") {
          if (step.kind === "name") {
            // Go back to params if symbol was parametric, otherwise to picker
            const spec = step.paramSpec;
            if (spec) {
              // `paramValues` holds only non-default values now, so anything
              // absent is at its default — blank would clear the field.
              const defaults = defaultsFor(spec);
              for (const [name, val] of Object.entries(step.paramValues)) {
                defaults[name] = paramValueToString(val).unwrapOr("");
              }
              setParamValues(defaults);
              setStep({
                kind: "params",
                symbol: step.symbol,
                paramSpec: spec,
              });
            } else {
              setStep({ kind: "pick-symbol" });
              setFilter("");
            }
          } else if (step.kind === "params") {
            setStep({ kind: "pick-symbol" });
            setFilter("");
          } else if (step.kind === "loading-params") {
            // Abandon the in-flight spec fetch; clearing the ref makes its
            // late reply a no-op.
            pendingSymbolRef.current = null;
            setStep({ kind: "pick-symbol" });
            setFilter("");
          } else {
            onClose();
          }
        } else {
          onClose();
        }
        return;
      }

      if (mode.kind === "new" && step.kind === "pick-symbol") {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex(
            allFiltered.length === 0
              ? 0
              : (selectedIndex + 1) % allFiltered.length
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex(
            allFiltered.length === 0
              ? 0
              : (selectedIndex - 1 + allFiltered.length) % allFiltered.length
          );
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (allFiltered.length > 0 && allFiltered[selectedIndex]) {
            selectSymbol(allFiltered[selectedIndex]);
          }
        }
      } else if (mode.kind === "new" && step.kind === "params") {
        if (e.key === "Enter") {
          e.preventDefault();
          confirmParams();
        }
      } else if (mode.kind === "new" && step.kind === "name") {
        if (e.key === "Enter") {
          e.preventDefault();
          void submitNewInstance();
        }
      } else if (mode.kind === "edit") {
        if (e.key === "Enter") {
          e.preventDefault();
          void submitEditInstance();
        }
      } else if (mode.kind === "rename") {
        if (e.key === "Enter") {
          e.preventDefault();
          void submitRenameInstance();
        }
      }
    },
    [
      mode,
      step,
      allFiltered,
      selectedIndex,
      selectSymbol,
      confirmParams,
      defaultsFor,
      submitNewInstance,
      submitEditInstance,
      submitRenameInstance,
      onClose,
    ]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Render a symbol row with two lines: name and description.
  const renderSymbolRow = (symbol: SymbolInfo, idx: number) => (
    <div
      key={`${symbol.name}-${idx}`}
      data-index={idx}
      className={
        "add-instance-row" +
        (idx === selectedIndex ? " add-instance-row-selected" : "")
      }
      role="option"
      aria-selected={idx === selectedIndex}
      onClick={() => selectSymbol(symbol)}
      onMouseEnter={() => setSelectedIndex(idx)}
    >
      <div className="add-instance-row-name">{symbol.name}</div>
      <div className="add-instance-row-description">{symbol.description}</div>
    </div>
  );

  // ---------------------------------------------------------------- //
  // Dialog title                                                       //
  // ---------------------------------------------------------------- //

  const ariaLabel =
    mode.kind === "new"
      ? "Add Instance"
      : mode.kind === "rename"
      ? "Rename Instance"
      : "Edit Instance Parameters";

  // ---------------------------------------------------------------- //
  // Render                                                             //
  // ---------------------------------------------------------------- //

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="command-palette add-instance-dialog"
        role="dialog"
        aria-label={ariaLabel}
      >
        {/* -------------------------------------------------------- */}
        {/* "new" mode: Step 1 — Symbol picker                        */}
        {/* -------------------------------------------------------- */}
        {mode.kind === "new" && step.kind === "pick-symbol" && (
          <>
            <input
              ref={inputRef}
              className="command-palette-input"
              type="text"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Search instances..."
              role="combobox"
              aria-expanded={true}
              aria-controls="add-instance-list"
              autoComplete="off"
              spellCheck={false}
            />
            <div
              ref={listRef}
              className="command-palette-list"
              id="add-instance-list"
              role="listbox"
            >
              {mode.symbols === null && !state.error && (
                <div className="command-palette-empty">Loading symbols...</div>
              )}
              {state.error && (
                <div
                  className="command-palette-empty"
                  style={{ color: "var(--color-ide-error)" }}
                >
                  {state.error}
                </div>
              )}
              {mode.symbols !== null && allFiltered.length === 0 && (
                <div className="command-palette-empty">No matching symbols</div>
              )}

              {hasSections && pdkSymbols.length > 0 && (
                <>
                  <div className="add-instance-section-header">PDK Symbols</div>
                  {pdkSymbols.map((s, i) => renderSymbolRow(s, i))}
                </>
              )}
              {hasSections && genericSymbols.length > 0 && (
                <>
                  <div className="add-instance-section-header">
                    Generic Symbols
                  </div>
                  {genericSymbols.map((s, i) =>
                    renderSymbolRow(s, i + pdkSymbols.length)
                  )}
                </>
              )}
              {!hasSections && allFiltered.map((s, i) => renderSymbolRow(s, i))}
            </div>
          </>
        )}

        {/* -------------------------------------------------------- */}
        {/* "new" mode: Step 1b — fetching the picked symbol's params */}
        {/* -------------------------------------------------------- */}
        {mode.kind === "new" && step.kind === "loading-params" && (
          <>
            <div className="command-palette-prompt-label">
              {step.symbol.name} — Parameters
            </div>
            <div className="add-instance-footer">
              <span className="add-instance-hint">
                Loading parameters… · Escape to cancel
              </span>
            </div>
          </>
        )}

        {/* -------------------------------------------------------- */}
        {/* "new" mode: Step 2 — Parameters                           */}
        {/* -------------------------------------------------------- */}
        {mode.kind === "new" && step.kind === "params" && (
          <>
            <div className="command-palette-prompt-label">
              {step.symbol.name} — Parameters
            </div>
            <ParamFields
              paramSpec={step.paramSpec}
              paramValues={paramValues}
              setParamValues={setParamValues}
              firstInputRef={inputRef}
            />
            {isLayoutDesign && Number(paramValues["m"] ?? 1) > 1 && (
              <ShapeField
                rectangular={rectangular}
                setRectangular={setRectangular}
              />
            )}
            <div className="add-instance-footer">
              <span className="add-instance-hint">
                Enter to confirm, Escape to go back
              </span>
              <button className="add-instance-btn" onClick={confirmParams}>
                Next
              </button>
            </div>
          </>
        )}

        {/* -------------------------------------------------------- */}
        {/* "new" mode: Step 3 — Instance name                        */}
        {/* -------------------------------------------------------- */}
        {mode.kind === "new" && step.kind === "name" && (
          <>
            <div className="command-palette-prompt-label">
              {step.symbol.name} — Instance Name
            </div>
            <input
              ref={inputRef}
              className="command-palette-input"
              type="text"
              value={instanceName}
              onChange={(e) => setInstanceName(e.target.value)}
              placeholder="Instance name (optional, auto-generated if blank)"
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
            />
            {error && (
              <div
                className="command-palette-empty"
                style={{ color: "var(--color-ide-error)" }}
              >
                {error}
              </div>
            )}
            <div className="add-instance-footer">
              <span className="add-instance-hint">
                {submitting
                  ? "Creating instance..."
                  : "Enter to create, Escape to go back"}
              </span>
              <button
                className="add-instance-btn"
                onClick={() => void submitNewInstance()}
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create"}
              </button>
            </div>
          </>
        )}

        {/* -------------------------------------------------------- */}
        {/* "edit" mode: parameter form                                */}
        {/* -------------------------------------------------------- */}
        {mode.kind === "edit" && (
          <>
            <div className="command-palette-prompt-label">
              {mode.symbolName
                ? `${mode.symbolName} — Edit Parameters`
                : "Edit Parameters"}
            </div>

            {/* Loading state */}
            {!mode.paramSpec && !state.error && (
              <div className="command-palette-empty">
                Loading instance details...
              </div>
            )}

            {/* Error from fetching instance details */}
            {(state.error || error) && (
              <div
                className="command-palette-empty"
                style={{ color: "var(--color-ide-error)" }}
              >
                {state.error ?? error}
              </div>
            )}

            {/* Parameter form */}
            {mode.paramSpec && !state.error && (
              <>
                <ParamFields
                  paramSpec={mode.paramSpec}
                  paramValues={paramValues}
                  setParamValues={setParamValues}
                  firstInputRef={inputRef}
                />
                {mode.currentRectangular !== undefined && (
                  <ShapeField
                    rectangular={rectangular}
                    setRectangular={setRectangular}
                  />
                )}
                <div className="add-instance-footer">
                  <span className="add-instance-hint">
                    {submitting
                      ? "Updating instance..."
                      : "Enter to update, Escape to cancel"}
                  </span>
                  <button
                    className="add-instance-btn"
                    onClick={() => void submitEditInstance()}
                    disabled={submitting}
                  >
                    {submitting ? "Updating..." : "Update"}
                  </button>
                </div>
              </>
            )}

            {/* Close button for error-only state (no params to edit) */}
            {state.error && (
              <div className="add-instance-footer">
                <span />
                <button className="add-instance-btn" onClick={onClose}>
                  Close
                </button>
              </div>
            )}
          </>
        )}

        {/* -------------------------------------------------------- */}
        {/* "rename" mode: instance name input                        */}
        {/* -------------------------------------------------------- */}
        {mode.kind === "rename" && (
          <>
            <div className="command-palette-prompt-label">Rename Instance</div>

            {/* Loading state */}
            {mode.currentName === null && !state.error && (
              <div className="command-palette-empty">
                Loading instance details...
              </div>
            )}

            {/* Error from fetching instance details */}
            {(state.error || error) && (
              <div
                className="command-palette-empty"
                style={{ color: "var(--color-ide-error)" }}
              >
                {state.error ?? error}
              </div>
            )}

            {/* Name input */}
            {mode.currentName !== null && !state.error && (
              <>
                <input
                  ref={inputRef}
                  className="command-palette-input"
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Instance name"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={submitting}
                />
                <div className="add-instance-footer">
                  <span className="add-instance-hint">
                    {submitting
                      ? "Renaming..."
                      : "Enter to rename, Escape to cancel"}
                  </span>
                  <button
                    className="add-instance-btn"
                    onClick={() => void submitRenameInstance()}
                    disabled={submitting}
                  >
                    {submitting ? "Renaming..." : "Rename"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
